import { create } from "zustand";
import { persist } from "zustand/middleware";

export type DockTab = "problems" | "runlog" | "events" | "deliveries";

export interface Toast {
  id: number;
  kind: "info" | "error" | "success";
  text: string;
}

interface SessionState {
  projectId: string | null;
  setProject: (id: string | null) => void;
  selectedFlights: string[];
  toggleFlight: (id: string) => void;
  setSelectedFlights: (ids: string[]) => void;
  activeFlight: string | null;
  setActiveFlight: (id: string | null) => void;
  currentRunId: string | null;
  setCurrentRun: (id: string | null) => void;
  /** dev-loop runs (Triggers / Analysis "Run + trace") also route reports through subscriptions.
   *  Deliberately not persisted: every reload starts with delivery off. */
  devDeliver: boolean;
  setDevDeliver: (on: boolean) => void;
  dockOpen: boolean;
  dockTab: DockTab;
  setDock: (open: boolean, tab?: DockTab) => void;
  theme: "dark" | "light";
  toggleTheme: () => void;
  railCollapsed: boolean;
  toggleRail: () => void;
  railWidth: number;
  setRailWidth: (w: number) => void;
  dockHeight: number;
  setDockHeight: (h: number) => void;
  toasts: Toast[];
  toast: (text: string, kind?: Toast["kind"]) => void;
  dismissToast: (id: number) => void;
  openTriggerFile: string | null;
  setOpenTriggerFile: (p: string | null) => void;
  analysisParams: string[];
  setAnalysisParams: (p: string[]) => void;
  liveParams: string[];
  setLiveParams: (p: string[]) => void;
  liveKey: string | null;
  setLiveKey: (k: string | null) => void;
  /** AI assistant side panel */
  assistantOpen: boolean;
  setAssistantOpen: (open: boolean) => void;
  assistantWidth: number;
  setAssistantWidth: (w: number) => void;
  /** project file the user is currently looking at (published by the editor pages) */
  activeFile: string | null;
  setActiveFile: (p: string | null) => void;
  /** one-shot request to open a file: the owning page consumes and clears it */
  pendingOpenFile: string | null;
  setPendingOpenFile: (p: string | null) => void;
}

let toastSeq = 1;

export const useSession = create<SessionState>()(
  persist(
    (set, get) => ({
      projectId: null,
      setProject: (id) => set({ projectId: id, currentRunId: null, openTriggerFile: null, activeFile: null, pendingOpenFile: null }),
      selectedFlights: [],
      toggleFlight: (id) => {
        const cur = get().selectedFlights;
        const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
        set({ selectedFlights: next, activeFlight: get().activeFlight ?? next[0] ?? null });
      },
      setSelectedFlights: (ids) => set({ selectedFlights: ids, activeFlight: ids[0] ?? null }),
      activeFlight: null,
      setActiveFlight: (id) => set({ activeFlight: id }),
      currentRunId: null,
      setCurrentRun: (id) => set({ currentRunId: id }),
      devDeliver: false,
      setDevDeliver: (on) => set({ devDeliver: on }),
      dockOpen: true,
      dockTab: "runlog",
      setDock: (open, tab) => set({ dockOpen: open, dockTab: tab ?? get().dockTab }),
      theme: "light",
      toggleTheme: () => set({ theme: get().theme === "dark" ? "light" : "dark" }),
      railCollapsed: false,
      toggleRail: () => set({ railCollapsed: !get().railCollapsed }),
      railWidth: 208,
      setRailWidth: (w) => set({ railWidth: Math.round(Math.min(420, Math.max(140, w))) }),
      dockHeight: 220,
      setDockHeight: (h) => set({ dockHeight: Math.round(Math.max(80, h)) }),
      toasts: [],
      toast: (text, kind = "info") => {
        const id = toastSeq++;
        set({ toasts: [...get().toasts, { id, kind, text }] });
        setTimeout(() => get().dismissToast(id), kind === "error" ? 8000 : 4000);
      },
      dismissToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
      openTriggerFile: null,
      setOpenTriggerFile: (p) => set({ openTriggerFile: p }),
      analysisParams: [],
      setAnalysisParams: (p) => set({ analysisParams: p }),
      liveParams: [],
      setLiveParams: (p) => set({ liveParams: p }),
      liveKey: null,
      setLiveKey: (k) => set({ liveKey: k }),
      assistantOpen: false,
      setAssistantOpen: (open) => set({ assistantOpen: open }),
      assistantWidth: 400,
      setAssistantWidth: (w) => set({ assistantWidth: Math.round(Math.min(900, Math.max(280, w))) }),
      activeFile: null,
      setActiveFile: (p) => (get().activeFile === p ? undefined : set({ activeFile: p })),
      pendingOpenFile: null,
      setPendingOpenFile: (p) => set({ pendingOpenFile: p }),
    }),
    {
      name: "reports-studio-session",
      partialize: (s) => ({
        projectId: s.projectId,
        selectedFlights: s.selectedFlights,
        activeFlight: s.activeFlight,
        currentRunId: s.currentRunId,
        theme: s.theme,
        railCollapsed: s.railCollapsed,
        railWidth: s.railWidth,
        dockHeight: s.dockHeight,
        dockOpen: s.dockOpen,
        dockTab: s.dockTab,
        openTriggerFile: s.openTriggerFile,
        analysisParams: s.analysisParams,
        liveParams: s.liveParams,
        liveKey: s.liveKey,
        assistantOpen: s.assistantOpen,
        assistantWidth: s.assistantWidth,
      }),
    },
  ),
);
